import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_URL,
    withCredentials: true, // Important for session cookies
    headers: {
        'Content-Type': 'application/json',
    },
    // Allow large multipart uploads (patient documents, signatures, etc.).
    // Defaults in axios cap browser/node uploads; we lift them to ~200 MB so
    // the request reaches the server (which enforces its own limits via multer).
    maxContentLength: 200 * 1024 * 1024,
    maxBodyLength: 200 * 1024 * 1024,
});

// Request interceptor to add auth token if available (or other headers)
api.interceptors.request.use(
    (config) => {
        // You can add headers here if needed (e.g. CSRF token)
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Global response interceptor to handle session timeouts
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Handle 401 errors (session expired/unauthorized)
        if (error.response && error.response.status === 401) {
            const url = error.config?.url || '';

            // Don't redirect for login-related endpoints to avoid loops or bad UX during login
            const publicEndpoints = [
                '/users/login',
                '/users/verify-otp',
                '/users/forgot-password',
                '/users/reset-password',
                '/users/session-status' // Don't redirect on status check, let the component handle it
            ];

            const isPublicEndpoint = publicEndpoints.some(endpoint => url.includes(endpoint));

            if (!isPublicEndpoint) {
                console.warn('Session expired or unauthorized. Emitting redirect event.');
                
                // Static import at top would be better, but keeping it inside to avoid circular check
                import('./sessionEvents.js').then(({ emitSessionExpired }) => {
                    emitSessionExpired('expired');
                });
            }
        }

        const reqUrl = error.config?.url || '';

        // Practice locked: read-only writes (measurements, etc.)
        if (error.response?.status === 403) {
            const code = error.response?.data?.code;
            if (code === 'PRACTICE_LOCKED_READ_ONLY') {
                const msg = error.response?.data?.message;
                if (msg) toast.warning(msg);
            }
        }

        // Payload too large — surface a friendly message instead of the
        // generic "Failed to ..." text so users know to remove/compress files.
        // Callers may set `suppressGlobal413Toast` on the axios config when they
        // show their own inline message (e.g. AddPatient per-document row errors).
        if (error.response?.status === 413 && !error.config?.suppressGlobal413Toast) {
            const serverMsg = error.response?.data?.message;
            toast.warning(
                serverMsg ||
                'Upload too large. Please reduce the number/size of attached files and try again.'
            );
        }

        // Login / session blocked when practice is locked (non-patient roles)
        if (error.response?.status === 423) {
            const code = error.response?.data?.code;
            const skipLockToast =
                reqUrl.includes('/users/login') ||
                reqUrl.includes('/users/verify-otp');
            if (code === 'PRACTICE_LOCKED' && !skipLockToast) {
                const msg = error.response?.data?.message;
                if (msg) toast.warning(msg);
            }
        }

        return Promise.reject(error);
    }
);

export default api;
